import type * as hast from 'hast';

import type * as api from '@gitbook/api-client';
import {
    FRAGMENT_ANNOTATION_BODY,
    INLINE_ANNOTATION,
    INLINE_BUTTON,
    INLINE_LINK,
    mention,
} from '@gitbook/doc-core';
import { Fragment, Inline, type Text } from '@gitbook/slate';

import type { ParsingContext } from '../context';
import type { HastToGastTransformMap } from './types';
import { getBooleanAttribute, getClassNamesAttribute, getStringAttribute } from './utils';

export const transformInlineLinks: HastToGastTransformMap = {
    a: async (node: hast.Element, eat, context, parsingContext) => {
        const { href } = node.properties || {};

        if (!node.children.length) {
            return [];
        }

        const children = await eat('inlines', node.children, context);

        if (typeof href !== 'string') {
            return children;
        }

        if (getBooleanAttribute(node.properties, 'dataFootnoteRef')) {
            return transformFootnoteBackRef(node, children, parsingContext);
        }

        const ref = await parsingContext.resolveLink(href);
        if (!ref) {
            return children;
        }

        const classNames = getClassNamesAttribute(node.properties);

        if (classNames.includes('button')) {
            return transformButton(node, children, classNames, ref);
        }

        if (
            // An HTML mention will have a "mention" property
            typeof node.properties?.dataMention === 'string' ||
            // A mention from Markdown will have a title of "mention".
            node.properties?.title === 'mention'
        ) {
            return [mention.createMention(ref)];
        }

        // When creating inline links:
        // - we set the page a link title if it exists
        if (parsingContext.onCreateInlineLink) {
            parsingContext.onCreateInlineLink(ref, node.properties);
        }

        return [
            Inline.create({
                type: INLINE_LINK,
                data: { ref },
                nodes: children,
            }),
        ];
    },
};

/**
 * Transform a footnote back reference link (<a class="footnote-backref">) into an annotation.
 */
async function transformFootnoteBackRef(
    node: hast.Element,
    children: (Inline | Text)[],
    parsingContext: ParsingContext
) {
    const { href } = node.properties || {};

    if (!href || typeof href !== 'string' || !href.startsWith('#')) {
        return children;
    }

    const id = href.slice(1);
    const fragmentNodes = await parsingContext.getDefinitionForFootnoteId(id);

    if (fragmentNodes.length === 0) {
        return children;
    }

    return [
        Inline.create({
            type: INLINE_ANNOTATION,
            nodes: children,
            fragments: [
                Fragment.create({
                    fragment: FRAGMENT_ANNOTATION_BODY,
                    type: FRAGMENT_ANNOTATION_BODY,
                    nodes: fragmentNodes,
                }),
            ],
        }),
    ];
}

/**
 * Transform an anchor element that should be a button.
 */
async function transformButton(
    node: hast.Element,
    children: (Inline | Text)[],
    classNames: string[],
    ref: api.ContentRef
) {
    const label = children.reduce((str, child) => `${str}${child.text}`, '');
    const icon = getStringAttribute(node.properties, 'dataIcon');

    return [
        Inline.create({
            type: INLINE_BUTTON,
            isVoid: true,
            data: {
                kind: classNames.includes('primary') ? 'primary' : 'secondary',
                label,
                ref,
                ...(icon ? { icon } : {}),
            },
        }),
    ];
}
